import React, { useState } from "react";
import { FiDownload } from "react-icons/fi";
import { complianceColor } from "./stpHelpers";

const PARAM_LIMITS = {
  ph: { min: 6.5, max: 8.5, unit: "" }, bod: { max: 20, unit: "mg/L" },
  cod: { max: 100, unit: "mg/L" }, tss: { max: 30, unit: "mg/L" },
  turbidity: { max: 10, unit: "NTU" }, chlorine: { min: 0.2, max: 1.5, unit: "mg/L" },
};

const StpCompliancePanel = ({ d }) => {
  const [activeView, setActiveView] = useState("Daily Log");
  const { compliance } = d;

  return (
    <div className="stp-compliance-panel">

      {/* Header */}
      <div className="stp-comp-header">
        <div>
          <div className="stp-section-title">Compliance & Audit Module</div>
          <div className="stp-comp-authority">{compliance.authorityName} · Permit: {compliance.permitNumber}</div>
        </div>
        <div className={`stp-comp-status-badge ${compliance.status === "Compliant" ? "stp-comp-ok" : "stp-comp-fail"}`}>
          {compliance.status === "Compliant" ? "✅ Compliant" : "❌ Non-Compliant"}
        </div>
      </div>

      {/* Audit info */}
      <div className="stp-comp-audit-row">
        <div className="stp-ca-item"><label>Last Audit</label><span>{compliance.lastAuditDate}</span></div>
        <div className="stp-ca-item"><label>Next Audit</label><span>{compliance.nextAuditDate}</span></div>
        <div className="stp-ca-item"><label>Violations (30d)</label>
          <span style={{ color: compliance.violations > 0 ? "#ef4444" : "#10b981", fontWeight: 700 }}>{compliance.violations}</span>
        </div>
        <button className="stp-ctrl-btn stp-ctrl-purple" style={{ display: "flex", gap: 5, alignItems: "center" }}>
          <FiDownload size={13} /> Export PDF Report
        </button>
      </div>

      {/* View toggle */}
      <div className="stp-view-row">
        {["Daily Log","Violation History","Parameter Limits"].map(v => (
          <button key={v} className={`stp-view-btn ${activeView === v ? "stp-view-active" : ""}`} onClick={() => setActiveView(v)}>{v}</button>
        ))}
      </div>

      {/* Daily Log */}
      {activeView === "Daily Log" && (
        <div className="stp-chart-card">
          <div className="stp-section-title">Daily Compliance Log</div>
          <table className="stp-table">
            <thead>
              <tr><th>Date</th><th>pH</th><th>BOD</th><th>COD</th><th>TSS</th><th>Turbidity</th><th>Cl₂</th><th>Status</th></tr>
            </thead>
            <tbody>
              {compliance.dailyLog.map((row, i) => (
                <tr key={i} className={!row.compliant ? "stp-tr-violation" : ""}>
                  <td style={{ fontSize: 12 }}>{row.date}</td>
                  <td style={{ color: complianceColor(row.ph, 6.5, 8.5), fontWeight: 600 }}>{row.ph}</td>
                  <td style={{ color: complianceColor(row.bod, 0, 20), fontWeight: 600 }}>{row.bod}</td>
                  <td style={{ color: complianceColor(row.cod, 0, 100), fontWeight: 600 }}>{row.cod}</td>
                  <td style={{ color: complianceColor(row.tss, 0, 30), fontWeight: 600 }}>{row.tss}</td>
                  <td style={{ color: complianceColor(row.turbidity, 0, 10), fontWeight: 600 }}>{row.turbidity}</td>
                  <td style={{ color: complianceColor(row.chlorine, 0.2, 1.5), fontWeight: 600 }}>{row.chlorine}</td>
                  <td>
                    <span className="stp-badge" style={{ background: row.compliant ? "#d1fae5" : "#fee2e2", color: row.compliant ? "#10b981" : "#ef4444" }}>
                      {row.compliant ? "✅" : "❌"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Violation History */}
      {activeView === "Violation History" && (
        <div className="stp-chart-card">
          <div className="stp-section-title">Violation History</div>
          {compliance.violationHistory.length === 0 ? (
            <div className="stp-empty">No violations in the last 30 days 🎉</div>
          ) : compliance.violationHistory.map((v, i) => (
            <div key={i} className="stp-violation-row">
              <div className="stp-vr-date">{v.date}</div>
              <div className="stp-vr-param">{v.parameter}</div>
              <div className="stp-vr-val">Measured: <strong style={{ color: "#ef4444" }}>{v.value}</strong></div>
              <div className="stp-vr-limit">Limit: {v.limit}</div>
              <div className="stp-vr-action">Action: {v.action}</div>
            </div>
          ))}
        </div>
      )}

      {/* Parameter Limits */}
      {activeView === "Parameter Limits" && (
        <div className="stp-chart-card">
          <div className="stp-section-title">MPCB Discharge Parameter Limits</div>
          <table className="stp-table">
            <thead>
              <tr><th>Parameter</th><th>Current Value</th><th>Limit</th><th>Margin</th><th>Status</th></tr>
            </thead>
            <tbody>
              {[
                { label: "pH",            val: d.outlet.ph,               min: 6.5, max: 8.5,  unit: ""      },
                { label: "BOD",           val: d.outlet.bod,              max: 20,              unit: "mg/L"  },
                { label: "COD",           val: d.outlet.cod,              max: 100,             unit: "mg/L"  },
                { label: "TSS",           val: d.outlet.tss,              max: 30,              unit: "mg/L"  },
                { label: "Turbidity",     val: d.outlet.turbidity,        max: 10,              unit: "NTU"   },
                { label: "TDS",           val: d.outlet.tds,              max: 2100,            unit: "mg/L"  },
                { label: "Residual Cl₂",  val: d.outlet.residualChlorine, min: 0.2, max: 1.5,  unit: "mg/L"  },
              ].map(r => {
                const col = complianceColor(r.val, r.min, r.max);
                const margin = r.max ? ((r.max - r.val) / r.max * 100).toFixed(0) : "—";
                return (
                  <tr key={r.label}>
                    <td>{r.label}</td>
                    <td style={{ fontWeight: 700, color: col }}>{r.val} {r.unit}</td>
                    <td style={{ color: "#6b7280" }}>{r.min ? `${r.min}–` : ""}{r.max} {r.unit}</td>
                    <td style={{ color: col }}>{margin !== "—" ? `${margin}%` : "—"}</td>
                    <td><span className="stp-badge" style={{ background: col + "18", color: col, fontSize: 11 }}>{col === "#10b981" ? "OK" : col === "#f59e0b" ? "Near" : "BREACH"}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StpCompliancePanel;
